import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import AddressGroup from 'components/field/AddressGroup';
import AutoComplete from 'components/field/AutoComplete';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import Accordion from 'components/global/Accordion';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { AUTOCOMPLETE, ENUMS, PRICE_CONFIGURATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import * as S from './PriceSettingsForm.styled';

/**
 *
 * @author @muhammedburakak
 */

const PriceSettingsForm = () => {
	const axiosPrivate = useAxiosPrivate();
	const { showError } = useAlert();

	const defaultColSpan = {
		md: 6
	};

	const formMethods = useFormContext();

	const {
		setValue,
		watch,
		formState: { errors, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	const fetchAutocompleteData = useCallback(async (props) => {
		const data = await axiosPrivate
			.post(PRICE_CONFIGURATIONS.GET_SHIPPING_CONTRACT_AUTOCOMPLETE_LIST)
			.catch((error) => {
				showError();
				AppLogger.error(error);
			});

		return data.data.filter((item) =>
			props?.keyword
				? item?.name?.toLowerCase().includes(props?.keyword.toLowerCase()) ||
					item?.deliveryCompanySettingsId?.toString().toLowerCase().includes(props?.keyword.toLowerCase())
				: props?.IDs
					? props?.IDs.includes(item.ID)
					: true
		);
	}, []);

	const shippingContractAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'deliveryCompanySettingsId', width: 50 },
			{ accessor: 'companyName', width: 100 },
			{ accessor: 'name', width: 200 }
		],
		[]
	);

	return (
		<S.AccordionContainer>
			<Accordion header="generalConfigs" defaultVisible>
				<FormItem colSpan={defaultColSpan} label="shippingContract" isLoading={isLoading} required>
					<AutoComplete
						fetchFunction={fetchAutocompleteData}
						columns={shippingContractAutocompleteColumns}
						value={watch('shippingContractID')}
						setValue={(value) => {
							setValue('shippingContractID', value?.ID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue('currency', value?.currency);
						}}
						name={`shippingContractID`}
						selectionLabelAccessor={['deliveryCompanySettingsId', 'name']}
					/>
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="serviceType" isLoading={isLoading} required>
					<EnumSelect
						searchable={false}
						name="serviceType"
						enumName={ENUMS.PRICE_CONFIGURATION_SERVICE_TYPE}
						setValue={(value) => {
							setValue(`serviceType`, value, { shouldValidate: true, shouldDirty: true });
						}}
						value={watch(`serviceType`)}
					/>
				</FormItem>

				<AddressGroup
					formFieldName="countryAddress"
					isLoading={isLoading}
					labels={{
						country: 'country'
					}}
					fieldsColspan={{
						address: 0,
						country: defaultColSpan,
						state: 0,
						city: 0,
						district: 0,
						zipCode: 0,
						lat: 0,
						lng: 0
					}}
					customTextFields={{
						country: false,
						state: true,
						city: false,
						district: true
					}}
				/>

				<FormItem colSpan={defaultColSpan} label="volumetricWeightRate" isLoading={isLoading} required>
					<IntegerInput name="volumetricWeightRate" />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="chargingStatus" isLoading={isLoading} required>
					<EnumSelect
						searchable={false}
						name="chargingStatus"
						enumName={ENUMS.PRICE_CONFIGURATION_CHARGING_STATUS}
						setValue={(value) => {
							setValue(`chargingStatus`, value, { shouldValidate: true, shouldDirty: true });
						}}
						value={watch(`chargingStatus`)}
					/>
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="basePriceMethodType" isLoading={isLoading} required>
					<EnumSelect
						searchable={false}
						name="basePriceMethodType"
						enumName={ENUMS.PRICE_CONFIGURATION_BASE_PRICE_METHOD_TYPE}
						setValue={(value) => {
							setValue(`basePriceMethodType`, value, { shouldValidate: true, shouldDirty: true });
						}}
						value={watch(`basePriceMethodType`)}
					/>
				</FormItem>
			</Accordion>
			<Accordion header="feasibilities" defaultVisible>
				<FormItem colSpan={defaultColSpan} label="isSameCity" isLoading={isLoading}>
					<SwitchInput
						className="mt-3"
						value={watch('isSameCity')}
						setValue={(event) => {
							const { checked: value } = event.target;
							setValue(`isSameCity`, value, { shouldValidate: true, shouldDirty: true });

							if (value) {
								setValue(`isBetweenCity`, false, { shouldValidate: true, shouldDirty: true });
							}
						}}
					/>
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="isBetweenCity" isLoading={isLoading}>
					<SwitchInput
						className="mt-3"
						value={watch('isBetweenCity')}
						setValue={(event) => {
							const { checked: value } = event.target;

							setValue(`isBetweenCity`, value, { shouldValidate: true, shouldDirty: true });

							if (value) {
								setValue(`isSameCity`, false, { shouldValidate: true, shouldDirty: true });
							}
						}}
					/>
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="shipmentType" isLoading={isLoading} required>
					<EnumSelect
						searchable={false}
						name="shipmentType"
						enumName={ENUMS.PRICE_CONFIGURATION_SHIPMENT_TYPE}
						setValue={(value) => {
							setValue(`shipmentType`, value, { shouldValidate: true, shouldDirty: true });
						}}
						value={watch(`shipmentType`)}
					/>
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="maxOrderValue" isLoading={isLoading} required>
					<IntegerInput name="maxOrderValue" allowFloat decimalLimit={1} unit={watch(`currency`)} />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="maxCodValue" isLoading={isLoading} required>
					<IntegerInput name="maxCodValue" allowFloat decimalLimit={1} unit={watch(`currency`)} />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="maxKm" isLoading={isLoading}>
					<IntegerInput name="maxKm" allowFloat decimalLimit={1} />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="maxWeight" isLoading={isLoading}>
					<IntegerInput name="maxWeight" allowFloat decimalLimit={1} />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="maxDimension" isLoading={isLoading}>
					<IntegerInput name="maxDimension" allowFloat decimalLimit={2} />
				</FormItem>
				<FormItem colSpan={defaultColSpan} label="maxPackageCount" isLoading={isLoading}>
					<IntegerInput name="maxPackageCount" />
				</FormItem>
				{watch('serviceType') === 'LOCKER_DELIVERY' && (
					<FormItem colSpan={defaultColSpan} label="lockerSizes" isLoading={isLoading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.LOCKERS}
							columns={[
								{ accessor: 'ID', width: 30 },
								{ accessor: 'name', width: 200 }
							]}
							value={watch('lockerIds')}
							setValue={(value) => {
								setValue(`lockerIds`, value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							selectionLabelAccessor={['name']}
							multipleSelection
						/>
					</FormItem>
				)}
			</Accordion>

			<Accordion header="priceConfigs">
				<FormItem colSpan={defaultColSpan} label="maxFreeWeight" isLoading={isLoading}>
					<IntegerInput name="maxFreeWeight" allowFloat decimalLimit={1} />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="extraWeightPerKg" isLoading={isLoading}>
					<IntegerInput name="extraWeightPerKg" allowFloat decimalLimit={1} unit={watch(`currency`)} />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="maxFreeKm" isLoading={isLoading}>
					<IntegerInput name="maxFreeKm" allowFloat decimalLimit={1} />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="extraChargePerKm" isLoading={isLoading}>
					<IntegerInput name="extraChargePerKm" allowFloat decimalLimit={1} unit={watch(`currency`)} />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="applyCodRateAfter" isLoading={isLoading}>
					<IntegerInput name="applyCodRateAfter" allowFloat decimalLimit={1} unit={watch(`currency`)} />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="codCharge" isLoading={isLoading}>
					<IntegerInput name="codCharge" allowFloat decimalLimit={1} unit={watch(`currency`)} />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="codPercentage" isLoading={isLoading}>
					<IntegerInput name="codPercentage" allowFloat />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="cardOnDeliveryPercentage" isLoading={isLoading}>
					<IntegerInput name="cardOnDeliveryPercentage" allowFloat />
				</FormItem>
				<FormItem
					colSpan={defaultColSpan}
					label="returnPercentage"
					isLoading={isLoading}
					required={watch('serviceType') !== 'IMPORT'}>
					<IntegerInput name="returnPercentage" allowFloat />
				</FormItem>
				<FormItem colSpan={defaultColSpan} label="fuelSurcharge" isLoading={isLoading}>
					<IntegerInput name="fuelSurcharge" allowFloat />
				</FormItem>
			</Accordion>

			<Accordion header="promotionConfigs">
				<FormItem colSpan={defaultColSpan} label="discountPercentage" isLoading={isLoading}>
					<IntegerInput name="discountPercentage" allowFloat />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="firstPriceRate" isLoading={isLoading}>
					<IntegerInput name="firstPriceRate" allowFloat />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="starterDiscountAmount" isLoading={isLoading}>
					<IntegerInput
						value={watch('starterDiscountAmount')}
						setValue={(value) => {
							setValue(`starterDiscountAmount`, value, { shouldValidate: true, shouldDirty: true });
							if (value) {
								setValue(`starterDiscountPercentage`, null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}
						}}
						errorMessage={errors?.starterDiscountAmount?.message}
						allowFloat
					/>
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="scaleDiscountAmount" isLoading={isLoading}>
					<IntegerInput
						value={watch('scaleDiscountAmount')}
						setValue={(value) => {
							setValue(`scaleDiscountAmount`, value, { shouldValidate: true, shouldDirty: true });
							if (value) {
								setValue(`scaleDiscountPercentage`, null, { shouldValidate: true, shouldDirty: true });
							}
						}}
						errorMessage={errors?.scaleDiscountAmount?.message}
						allowFloat
					/>
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="starterDiscountPercentage" isLoading={isLoading}>
					<IntegerInput
						value={watch('starterDiscountPercentage')}
						setValue={(value) => {
							setValue(`starterDiscountPercentage`, value, { shouldValidate: true, shouldDirty: true });
							if (value) {
								setValue(`starterDiscountAmount`, null, { shouldValidate: true, shouldDirty: true });
							}
						}}
						errorMessage={errors?.starterDiscountPercentage?.message}
						allowFloat
					/>
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="scaleDiscountPercentage" isLoading={isLoading}>
					<IntegerInput
						value={watch('scaleDiscountPercentage')}
						setValue={(value) => {
							setValue(`scaleDiscountPercentage`, value, { shouldValidate: true, shouldDirty: true });
							if (value) {
								setValue(`scaleDiscountAmount`, null, { shouldValidate: true, shouldDirty: true });
							}
						}}
						errorMessage={errors?.scaleDiscountPercentage?.message}
						allowFloat
					/>
				</FormItem>
			</Accordion>
		</S.AccordionContainer>
	);
};

export default PriceSettingsForm;
